import { NSARouterMakerResult } from "namirasoft-account-react";
import { ChatInferencerType, InferencerType, LoadBalancerAlgorithm, LoadBalancerFullRow, LoadBalancerInputRow, LoadBalancerRow, LoadBalancerTargetInputRow } from "namirasoft-inference";
import { NSBox, NSBoxEnum, NSBoxString, NSBoxTextArea, NSColumn, NSRepeater, NSRow, NSSpace, NSSpaceSizeType } from "namirasoft-site-react";
import { Component, createRef, Fragment } from "react";
import ChatList from "../components/ChatList";
import InferencerCommonFields from "../components/InferencerCommonFields";
import InferencerGraph from "../components/InferencerGraph";
import InferencerLoader from "../components/InferencerLoader";
import InferencerVisualizer from "../components/InferencerVisualizer";
import LoadBalancerTargetLine from "../components/LoadBalancerTargetLine";
import LoadBalancerTargetsView from "../components/LoadBalancerTargetsView";
import UsageChart from "../components/UsageChart";
import { InferenceEntities } from "../InferenceEntities";
import { InferenceInfo } from "../InferenceInfo";
import { InferenceLayout } from '../InferenceLayout';
import { InferenceRouterProps } from "../InferenceRouterProps";

interface LoadBalancerEditPageProps extends NSARouterMakerResult, InferenceRouterProps
{
    isEdit: boolean;
}

export default class LoadBalancerEditPage extends Component<LoadBalancerEditPageProps>
{
    NSRepeater_Targets = createRef<NSRepeater<LoadBalancerTargetLine, LoadBalancerTargetInputRow>>();
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxEnum_Algorithm = createRef<NSBoxEnum<LoadBalancerAlgorithm>>();
    InferencerCommonFields_Ids = createRef<InferencerCommonFields>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    override render()
    {
        return (
            <InferenceLayout<LoadBalancerRow, LoadBalancerInputRow>
                {...this.props}
                action={{ description: this.props.isEdit ? InferenceInfo.load_balancer.edit : InferenceInfo.load_balancer.create }}
                pages={{
                    entity: InferenceEntities.load_balancer(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_view: {
                                tabs: {
                                    extra: {
                                        after_general: [
                                            {
                                                title: "Chats",
                                                clicked: false,
                                                isVisible: (model) => !!model.id,
                                                getContent: (model) => model.id
                                                    ? <ChatList
                                                        {...this.props}
                                                        inferencer_type={ChatInferencerType.LOAD_BALANCER}
                                                        inferencer_id={model.id}
                                                    />
                                                    : null,
                                            },
                                            {
                                                title: "Usage",
                                                clicked: false,
                                                isVisible: (model) => !!model.id,
                                                getContent: (model) => model.id
                                                    ? <UsageChart
                                                        {...this.props}
                                                        inferencer_type={InferencerType.LOAD_BALANCER}
                                                        inferencer_id={model.id}
                                                    />
                                                    : null,
                                            },
                                        ],
                                    },
                                },
                            },
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): LoadBalancerInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let algorithm = this.NSBoxEnum_Algorithm.current?.getValueEnumOne(LoadBalancerAlgorithm, LoadBalancerAlgorithm.Random) ?? LoadBalancerAlgorithm.Random;
                                        let { validator_id, budget_id, memory_id, wiper_id, log_group_id } = this.InferencerCommonFields_Ids.current?.getValue() ?? InferencerCommonFields.EMPTY;
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? null;
                                        let targets = this.NSRepeater_Targets.current?.getValue() ?? [];

                                        return {
                                            workspace_id: null,
                                            name,
                                            algorithm,
                                            validator_id,
                                            budget_id,
                                            memory_id,
                                            wiper_id,
                                            log_group_id,
                                            description,
                                            targets,
                                            load_balancer_category: [],
                                            load_balancer_field: [],
                                            load_balancer_tag: [],
                                        };
                                    },
                                    setRow: (row: LoadBalancerRow) =>
                                    {
                                        let full = row as LoadBalancerFullRow;

                                        this.NSBoxString_Name.current?.setValue(full.name);
                                        this.NSBoxEnum_Algorithm.current?.setValue(full.algorithm);
                                        this.InferencerCommonFields_Ids.current?.setValue(full);
                                        this.NSBoxTextArea_Description.current?.setValue(full.description);
                                        this.NSRepeater_Targets.current?.setValue((full.targets ?? []).map(t => ({
                                            inferencer_type: t.inferencer_type,
                                            inferencer_id: t.inferencer_id,
                                            weight: t.weight,
                                        })));
                                    }
                                },
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "Targets",
                                            clicked: true,
                                            getContent: () => (
                                                <NSRepeater<LoadBalancerTargetLine, LoadBalancerTargetInputRow>
                                                    ref={this.NSRepeater_Targets}
                                                    title="Targets"
                                                    required
                                                    min={1}
                                                    createItem={(gIndex: number, lIndex: number, ref: React.RefObject<LoadBalancerTargetLine>, onChange: () => void, onDelete: undefined | ((index: number) => void)) =>
                                                    {
                                                        return <LoadBalancerTargetLine
                                                            ref={ref}
                                                            {...this.props}
                                                            key={`TargetLine_${gIndex}`}
                                                            index={lIndex}
                                                            deletable={true}
                                                            onChanged={() => { onChange(); }}
                                                            onDelete={() => { onDelete?.(lIndex); }}
                                                        />;
                                                    }}
                                                    isEmpty={function (_, value: LoadBalancerTargetInputRow | null): boolean
                                                    {
                                                        return !LoadBalancerTargetLine.IS_DIRTY(value);
                                                    }}
                                                    getValue={function (item: LoadBalancerTargetLine, checkError: boolean): LoadBalancerTargetInputRow | null
                                                    {
                                                        return item.getValue(checkError);
                                                    }}
                                                    setValue={function (item: LoadBalancerTargetLine, _: number, value: LoadBalancerTargetInputRow | null): void
                                                    {
                                                        if (value)
                                                            item.setValue(value);
                                                    }}
                                                    style={{ width: NSBox.width.triple }}
                                                />
                                            )
                                        },
                                        {
                                            title: "Common",
                                            clicked: true,
                                            getContent: () => (
                                                <InferencerCommonFields ref={this.InferencerCommonFields_Ids} {...this.props} info_entity="load-balancer" />
                                            ),
                                        },
                                    ]
                                }
                            },
                            getChildrenAfter: (id, row) =>
                            {
                                if (!id || !row)
                                    return null;
                                let full = row as LoadBalancerFullRow;
                                return (
                                    <Fragment>
                                        <LoadBalancerTargetsView
                                            {...this.props}
                                            load_balancer_id={id}
                                            rows={full.targets ?? []}
                                        />
                                        <NSSpace size={NSSpaceSizeType.SMALL} />
                                        <InferencerLoader
                                            {...this.props}
                                            inferencer_type={InferencerType.LOAD_BALANCER}
                                            inferencer_id={id}
                                            render={({ tree, loading, error }) => (
                                                <Fragment>
                                                    <InferencerGraph tree={tree} loading={loading} error={error} />
                                                    <NSSpace size={NSSpaceSizeType.SMALL} />
                                                    <InferencerVisualizer tree={tree} loading={loading} error={error} />
                                                </Fragment>
                                            )}
                                        />
                                    </Fragment>
                                );
                            },
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_Name}
                            required
                            title="Name"
                            info={{ link: "https://namirasoft.com/inference/console-guide/load-balancer/#field-name" }}
                        />
                        <NSBoxEnum<LoadBalancerAlgorithm>
                            ref={this.NSBoxEnum_Algorithm}
                            title="Algorithm"
                            required
                            multiple={false}
                            info={{ link: "https://namirasoft.com/inference/console-guide/load-balancer/#field-algorithm" }}
                            getEnumObject={() => LoadBalancerAlgorithm}
                        />
                    </NSRow>
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        title="Description"
                        required={false}
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/inference/console-guide/load-balancer/#field-description" }}
                    />
                </NSColumn>
            </InferenceLayout>
        );
    }
}